import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ParticipationModal = ({ challenge, isOpen, onClose, onConfirm }) => {
  const [amount, setAmount] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState('monthly');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [message, setMessage] = useState('');

  const quickAmounts = [25, 50, 100, 250, 500];

  const handleAmountSelect = (value) => {
    setSelectedAmount(value);
    setAmount(value?.toString());
  };

  const handleCustomAmount = (e) => {
    const value = e?.target?.value;
    setAmount(value);
    setSelectedAmount(null);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (amount && parseFloat(amount) > 0) {
      onConfirm({
        challengeId: challenge?.id,
        amount: parseFloat(amount),
        isRecurring,
        recurringFrequency: isRecurring ? recurringFrequency : null,
        isAnonymous,
        message: message?.trim()
      });
      
      // Reset form
      setAmount('');
      setSelectedAmount(null);
      setIsRecurring(false);
      setRecurringFrequency('monthly');
      setIsAnonymous(false);
      setMessage('');
    }
  };

  const formatAmount = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-card-foreground">
              Join Challenge
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {challenge?.title}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Quick Amount Selection */}
          <div>
            <label className="text-sm font-medium text-card-foreground mb-3 block">
              Select Amount
            </label>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {quickAmounts?.map((value) => (
                <Button
                  key={value}
                  type="button"
                  variant={selectedAmount === value ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleAmountSelect(value)}
                  className="font-mono"
                >
                  ${value}
                </Button>
              ))}
            </div>
            <Input
              type="number"
              label="Custom Amount"
              placeholder="Enter amount"
              value={amount}
              onChange={handleCustomAmount}
              className="font-mono"
              min="1"
              step="1"
            />
          </div>

          {/* Recurring Donation */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="recurring"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e?.target?.checked)}
                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary focus:ring-2"
              />
              <label htmlFor="recurring" className="text-sm font-medium text-card-foreground">
                Make this a recurring donation
              </label>
            </div>
            
            {isRecurring && (
              <select
                value={recurringFrequency}
                onChange={(e) => setRecurringFrequency(e?.target?.value)}
                className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            )}
          </div>

          {/* Anonymous Donation */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="anonymous"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e?.target?.checked)}
              className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary focus:ring-2"
            />
            <label htmlFor="anonymous" className="text-sm font-medium text-card-foreground">
              Donate anonymously
            </label>
          </div>

          {/* Message */}
          <div>
            <label className="text-sm font-medium text-card-foreground mb-2 block">
              Message (Optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              placeholder="Share why this cause matters to you..."
              rows={3}
              className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              maxLength={200}
            />
            <div className="text-xs text-muted-foreground mt-1">
              {message?.length}/200 characters
            </div>
          </div>

          {/* Summary */}
          {amount && parseFloat(amount) > 0 && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Donation Amount:</span>
                <span className="text-sm font-medium text-card-foreground">
                  {formatAmount(parseFloat(amount))}
                </span>
              </div>
              {isRecurring && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Frequency:</span>
                  <span className="text-sm font-medium text-card-foreground capitalize">
                    {recurringFrequency}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Visibility:</span>
                <span className="text-sm font-medium text-card-foreground">
                  {isAnonymous ? 'Anonymous' : 'Public'}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!amount || parseFloat(amount) <= 0}
              className="flex-1"
              iconName="Heart"
              iconPosition="left"
            >
              Donate {amount ? formatAmount(parseFloat(amount)) : '$0'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ParticipationModal;